import type { BgId, MediaRef, ProfileDoc, Widget } from '../contract.js';
import { copy } from '../copy.js';
import { findIt, place, resizeTarget, stepTarget, panned, zoomed } from '../grid/engine.js';
import type { Cell, WidgetSpec } from '../grid/engine.js';
import { nextTextSize } from '../grid/text.js';
import { editDoc } from './derive.js';
import { docMut, docNext, patchW } from './doc.js';
import { uiError } from './store.js';
import type { AppStore } from './store.js';

/** Editing the widgets of your draft: add, remove, move, resize, reframe, restyle (04 §3–§11). */

let serial = 0;
const newId = (): string => { serial += 1; return `w${Date.now().toString(36)}_${serial}`; };

/** `addWidget`: place a new widget at `cell` or the first free spot; a toast when there is no room. */
export function addWidget(s: AppStore, spec: WidgetSpec, cell: Cell | null = null): string | null {
  const st = s.getState(), doc = editDoc(st);
  if (!doc || doc.saving) return null;
  const spot = place(doc.items, spec, cell);
  if (!spot) { s.set({ tray: null, dropCell: null }); s.flash(copy.toast.noSpace); return null; }
  const id = newId(), widget: Widget = { px: 0.5, py: 0.5, z: 1, ...spec, ...spot, id };
  docMut(s, (draft) => { draft.items.push(widget); }, { sel: id, tray: null, swOpen: false, dropCell: null, txt: widget.t === 'text' ? id : null });
  return id;
}
/** The + of the island: opens or closes the add menu. */
export function toggleAdd(s: AppStore): void {
  const st = s.getState(), doc = editDoc(st);
  if (doc && !doc.saving) s.set({ tray: st.tray === 'add' ? null : 'add', sel: null, txt: null });
}
export const addText = (s: AppStore): string | null => addWidget(s, { t: 'text', text: '' });
/** Add menu, Song: one song per profile; it starts as the second song of the list, as in the prototype. */
export function addSong(s: AppStore): string | null {
  const st = s.getState(), doc = editDoc(st);
  if (!doc || doc.items.some((item) => item.t === 'song')) return null;
  const song = st.songs[1] ?? st.songs[0];
  return song ? addWidget(s, { t: 'song', song }) : null;
}

export function removeW(s: AppStore, id: string): void {
  docMut(s, (draft) => { draft.items = draft.items.filter((item) => item.id !== id); }, { sel: null, txt: null, swOpen: false });
}
export function zoomW(s: AppStore, id: string, factor: number): void {
  docMut(s, (draft) => { for (const item of draft.items) if (item.id === id) item.z = zoomed(item.z, factor); });
}
export function panBy(s: AppStore, id: string, dx: number, dy: number): void {
  docMut(s, (draft) => { for (const item of draft.items) if (item.id === id) Object.assign(item, panned(item, dx, dy)); });
}
/** Change song: the next song of the list after the current one. */
export function changeSong(s: AppStore, id: string): void {
  const songs = s.getState().songs;
  if (!songs.length) return;
  docMut(s, (draft) => {
    for (const item of draft.items) if (item.id === id) {
      const current = songs.findIndex((song) => !!item.song && song.t === item.song.t);
      item.song = songs[(current + 1) % songs.length] ?? null;
    }
  });
}
/** Arrow keys: one cell, jumping over widgets in the way. */
export function stepW(s: AppStore, id: string, dc: number, dr: number): void {
  const doc = editDoc(s.getState());
  if (!doc || doc.saving) return;
  const item = findIt(doc.items, id), target = item ? stepTarget(doc.items, item, dc, dr) : null;
  if (target) patchW(s, id, target);
}
/** Shift + arrow keys: one cell wider, narrower, taller or shorter, when it fits. */
export function resizeBy(s: AppStore, id: string, dw: number, dh: number): void {
  const doc = editDoc(s.getState());
  if (!doc || doc.saving) return;
  const item = findIt(doc.items, id), target = item ? resizeTarget(doc.items, item, dw, dh) : null;
  if (target) patchW(s, id, target);
}
export function textSize(s: AppStore, id: string, dir: -1 | 1): void {
  const doc = editDoc(s.getState());
  const item = doc ? findIt(doc.items, id) : null, next = item ? nextTextSize(item, dir) : null;
  if (next != null) patchW(s, id, { fs: next });
}
export const setBg = (s: AppStore, id: string, bg: BgId): void => patchW(s, id, { bg });

// ------------------------------------------------------------------ files (04 §11.7)

/** Change a widget of the draft whatever its status: uploads may finish while the draft is being saved. */
function patchDraftItem(s: AppStore, id: string, patch: Partial<Widget> | null): void {
  const st = s.getState();
  const next = (items: readonly Widget[]): Widget[] => patch ? items.map((item) => (item.id === id ? { ...item, ...patch } : item)) : items.filter((item) => item.id !== id);
  if (st.view === 'compose' && st.draft && st.draft.items.some((item) => item.id === id)) s.set({ draft: { ...st.draft, items: next(st.draft.items) } });
  else if (st.pe && st.pe.items.some((item) => item.id === id)) s.set({ pe: { ...st.pe, items: next(st.pe.items) } });
}
function uploadDone(s: AppStore, id: string): void {
  const { [id]: _done, ...rest } = s.getState().uploading;
  s.set({ uploading: rest });
}
const localUrl = (file: File): string | null => { try { return URL.createObjectURL(file); } catch { return null; } };

/** Read the real aspect ratio (and a video's length) from the local preview, so framing is right at once. */
function measure(s: AppStore, id: string, url: string, video: boolean): void {
  if (typeof document === 'undefined') return;
  if (!video) {
    const image = new Image();
    image.onload = () => { if (image.naturalWidth && image.naturalHeight) patchDraftItem(s, id, { ar: image.naturalWidth / image.naturalHeight }); };
    image.src = url;
    return;
  }
  const element = document.createElement('video');
  element.preload = 'metadata';
  element.onloadedmetadata = () => patchDraftItem(s, id, {
    ar: element.videoWidth && element.videoHeight ? element.videoWidth / element.videoHeight : 16 / 9,
    len: Math.max(1, Math.round(Number.isFinite(element.duration) ? element.duration : 10)),
  });
  element.src = url;
}

/**
 * `addFiles`: picked or dropped files become photo and video widgets. Each shows its local preview
 * at once and starts uploading (the pack's upload rule). The first file goes to `cell` when dropped.
 */
export function addFiles(s: AppStore, files: readonly File[], cell: Cell | null): void {
  const st = s.getState(), doc = editDoc(st);
  if (!doc || doc.saving) return;
  const allowVideo = st.caps.video, rejected: string[] = [];
  const specs: { id: string; file: File; video: boolean; url: string | null; spec: WidgetSpec }[] = [];
  for (const file of files) {
    const type = file.type || '', image = type.startsWith('image/'), video = type.startsWith('video/');
    if (!image && !(video && allowVideo)) { rejected.push(file.name || ''); continue; }
    const url = localUrl(file);
    // A GIF is the one picture that moves: only it can be held (Media.tsx).
    specs.push({ id: newId(), file, video, url, spec: { t: video ? 'video' : 'photo', kind: video ? 'video' : 'square', url, name: file.name || '', ...(video ? { len: 10 } : {}), ...(type === 'image/gif' ? { anim: true } : {}) } });
  }
  const placed: typeof specs = [];
  const patch = specs.length ? docNext(st, (draft) => {
    specs.forEach((item, index) => {
      const spot = place(draft.items, item.spec, index === 0 ? cell : null);
      if (!spot) return;
      draft.items.push({ px: 0.5, py: 0.5, z: 1, ...item.spec, ...spot, id: item.id });
      placed.push(item);
    });
  }) : null;
  const last = placed[placed.length - 1];
  const uploading = { ...st.uploading };
  for (const item of placed) uploading[item.id] = true;
  s.set({ ...(patch ?? {}), dropCell: null, tray: null, uploading, ...(last ? { sel: last.id, txt: null, swOpen: false } : {}) });
  const first = rejected[0], missed = specs.length - placed.length;
  if (first !== undefined) s.flash(allowVideo ? copy.toast.wrongType(first) : copy.toast.photosOnly(first));
  else if (missed > 0) s.flash(copy.toast.noSpaceFiles(missed));
  const purpose = doc.kind === 'profile' ? 'profile' : 'post';
  for (const item of placed) {
    if (item.url) measure(s, item.id, item.url, item.video);
    void s.trackUpload(s.data.uploadMedia(item.file, purpose)).then((ref: MediaRef) => {
      uploadDone(s, item.id);
      // The local preview stays on screen when the backend names the media by id; otherwise its address is the reference.
      patchDraftItem(s, item.id, {
        ...(ref.mediaId ? { mediaId: ref.mediaId } : {}), ...(!ref.mediaId || !item.url ? { url: ref.url } : {}),
        ...(ref.ar ? { ar: ref.ar } : {}), ...(ref.len ? { len: ref.len } : {}), ...(ref.name ? { name: ref.name } : {}),
      });
    }, (reason: unknown) => {
      s.report(uiError(reason));
      s.uploadFailures += 1;
      uploadDone(s, item.id);
      const now = s.getState();
      patchDraftItem(s, item.id, null);
      if (now.sel === item.id) s.set({ sel: null, swOpen: false });
      s.flash(copy.toast.uploadFailed(item.file.name || ''));
    });
  }
}

/** Look tray, Upload: the chosen image becomes your picture at once and is uploaded. */
export function setPicture(s: AppStore, file: File): void {
  const st = s.getState();
  if (!st.pe || st.peStatus === 'saving') return;
  if (!(file.type || '').startsWith('image/')) { s.flash(copy.toast.photosOnly(file.name || '')); return; }
  // A GIF goes on a grid, not into your picture (owner, 4 October 2026).
  if (file.type === 'image/gif') { s.flash(copy.toast.gifPicture(file.name || '')); return; }
  const previous: ProfileDoc['pic'] = st.pe.pic, preview = localUrl(file);
  if (preview) docMut(s, (draft) => { if ('pic' in draft) draft.pic = { url: preview }; });
  const applyPic = (pic: ProfileDoc['pic'], only: string | null) => {
    const pe = s.getState().pe;
    if (!pe) return;
    if (only !== null && !(typeof pe.pic === 'object' && pe.pic.url === only)) return;
    s.set({ pe: { ...pe, pic } });
  };
  void s.trackUpload(s.data.uploadMedia(file, 'picture')).then((ref: MediaRef) => {
    applyPic({ url: ref.url, ...(ref.mediaId ? { mediaId: ref.mediaId } : {}) }, preview);
  }, (reason: unknown) => {
    s.report(uiError(reason));
    s.uploadFailures += 1;
    applyPic(previous, preview);
    s.flash(copy.toast.uploadFailed(file.name || ''));
  });
}

/**
 * Try again on a photo or video that did not load: load it again and fetch its post or profile
 * afresh, because the usual cause is a media address that has expired.
 */
export function retryMedia(s: AppStore, key: string): void {
  const st = s.getState();
  s.set({ mediaRetry: { ...st.mediaRetry, [key]: (st.mediaRetry[key] ?? 0) + 1 } });
  if (st.view === 'person') void s.loadDay(st.date, true);
  else if (st.view === 'profile' && st.profileOf && !st.pe) void s.loadProfile(st.profileOf, true);
}
